/**
 * Graph view settings.
 *
 * Global and local graphs keep FULLY SEPARATE settings, as in Obsidian: a
 * filter typed in the global graph must not follow you into the right panel.
 *
 * Device-local UI preference, like hn.ui. Nothing here syncs, and node
 * positions are deliberately NOT here: a few thousand live coordinates would
 * write megabytes to localStorage on every simulation tick.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { newId } from "@hydra-notes/shared";
import { resetPositions } from "@/graph/positions";

export type GraphScope = "global" | "local";
export type GraphSection = "filters" | "groups" | "display" | "forces" | "local";

export interface GraphFilters {
  search: string;
  showTags: boolean;
  showAttachments: boolean;
  /** Obsidian's "Existing files only": hide unresolved-link phantoms. */
  existingOnly: boolean;
  showOrphans: boolean;
}

export interface GraphGroup {
  id: string;
  query: string;
  /** Free-form #rrggbb. User data, deliberately not a token (CLAUDE.md 5). */
  color: string;
}

export interface GraphDisplay {
  showArrows: boolean;
  /** Default coloring: hue notes by their top-level folder (groups override). */
  colorByFolder: boolean;
  /** Zoom at which labels fade in. Geometric, see graph/labels.ts. */
  textFadeMultiplier: number;
  nodeSizeMultiplier: number;
  lineSizeMultiplier: number;
}

export interface GraphForces {
  centerStrength: number;
  repelStrength: number;
  linkStrength: number;
  linkDistance: number;
}

export interface GraphLocalOptions {
  /** Obsidian: localJumps. */
  depth: number;
  /** Obsidian: localBacklinks. */
  incoming: boolean;
  /** Obsidian: localForelinks. */
  outgoing: boolean;
  /** Obsidian: localInterlinks. Edges between neighbours the walk did not use. */
  neighborLinks: boolean;
}

export interface GraphSettings {
  filters: GraphFilters;
  groups: GraphGroup[];
  display: GraphDisplay;
  forces: GraphForces;
}

export interface LocalGraphSettings extends GraphSettings {
  local: GraphLocalOptions;
}

export const DEFAULT_FILTERS: GraphFilters = {
  search: "",
  showTags: false,
  showAttachments: false,
  existingOnly: false,
  showOrphans: true,
};

export const DEFAULT_DISPLAY: GraphDisplay = {
  showArrows: false,
  colorByFolder: true,
  textFadeMultiplier: 0,
  nodeSizeMultiplier: 1,
  lineSizeMultiplier: 1,
};

export const DEFAULT_FORCES: GraphForces = {
  /** Obsidian's literal default. */
  centerStrength: 0.518713248970729,
  repelStrength: 10,
  linkStrength: 1,
  linkDistance: 250,
};

export const DEFAULT_LOCAL: GraphLocalOptions = {
  depth: 1,
  incoming: true,
  outgoing: true,
  neighborLinks: false,
};

/** [min, max, step] for every numeric control. */
export const RANGES = {
  textFadeMultiplier: [-3, 3, 0.1],
  nodeSizeMultiplier: [0.1, 5, 0.1],
  lineSizeMultiplier: [0.1, 5, 0.1],
  centerStrength: [0, 1, 0.01],
  repelStrength: [0, 20, 0.1],
  linkStrength: [0, 1, 0.01],
  linkDistance: [30, 500, 1],
  depth: [1, 5, 1],
} as const;

export const MAX_GROUPS = 32;

/** Starter ring, so a brand new group is never invisible against the canvas. */
const GROUP_SEED_COLORS = ["#e05c6e", "#e0a33c", "#3cc98a", "#3c9ee0", "#a06ce0", "#e06cae"];

const freshSettings = (): GraphSettings => ({
  filters: { ...DEFAULT_FILTERS },
  groups: [],
  display: { ...DEFAULT_DISPLAY },
  forces: { ...DEFAULT_FORCES },
});

const freshLocal = (): LocalGraphSettings => ({ ...freshSettings(), local: { ...DEFAULT_LOCAL } });

export const DEFAULT_GLOBAL: GraphSettings = freshSettings();
export const DEFAULT_LOCAL_SETTINGS: LocalGraphSettings = freshLocal();

type RangeKey = keyof typeof RANGES;

export function clampTo(key: RangeKey, v: unknown, fallback: number): number {
  const [min, max] = RANGES[key];
  const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
  return Math.min(max, Math.max(min, n));
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

const HEX_RE = /^#[0-9a-f]{6}$/i;

function hydrateGroups(raw: unknown): GraphGroup[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: GraphGroup[] = [];
  for (const g of raw) {
    if (!g || typeof g !== "object") continue;
    const rec = g as Partial<GraphGroup>;
    const color = typeof rec.color === "string" && HEX_RE.test(rec.color) ? rec.color : GROUP_SEED_COLORS[0]!;
    let id = typeof rec.id === "string" && rec.id ? rec.id : newId();
    if (seen.has(id)) id = newId();
    seen.add(id);
    out.push({ id, query: typeof rec.query === "string" ? rec.query : "", color });
    if (out.length >= MAX_GROUPS) break;
  }
  return out;
}

/**
 * Version-agnostic on purpose: deep-merge over defaults key by key, so a
 * future added setting appears at its default with no version bump. Clamping
 * is not cosmetic; a hand-edited linkDistance of 0 would freeze the sim.
 */
function hydrateSettings(raw: unknown, base: GraphSettings): GraphSettings {
  const r = (raw ?? {}) as Partial<GraphSettings>;
  const f = (r.filters ?? {}) as Partial<GraphFilters>;
  const d = (r.display ?? {}) as Partial<GraphDisplay>;
  const fo = (r.forces ?? {}) as Partial<GraphForces>;
  return {
    filters: {
      search: typeof f.search === "string" ? f.search : base.filters.search,
      showTags: bool(f.showTags, base.filters.showTags),
      showAttachments: bool(f.showAttachments, base.filters.showAttachments),
      existingOnly: bool(f.existingOnly, base.filters.existingOnly),
      showOrphans: bool(f.showOrphans, base.filters.showOrphans),
    },
    groups: hydrateGroups(r.groups),
    display: {
      showArrows: bool(d.showArrows, base.display.showArrows),
      colorByFolder: bool(d.colorByFolder, base.display.colorByFolder),
      textFadeMultiplier: clampTo("textFadeMultiplier", d.textFadeMultiplier, base.display.textFadeMultiplier),
      nodeSizeMultiplier: clampTo("nodeSizeMultiplier", d.nodeSizeMultiplier, base.display.nodeSizeMultiplier),
      lineSizeMultiplier: clampTo("lineSizeMultiplier", d.lineSizeMultiplier, base.display.lineSizeMultiplier),
    },
    forces: {
      centerStrength: clampTo("centerStrength", fo.centerStrength, base.forces.centerStrength),
      repelStrength: clampTo("repelStrength", fo.repelStrength, base.forces.repelStrength),
      linkStrength: clampTo("linkStrength", fo.linkStrength, base.forces.linkStrength),
      linkDistance: clampTo("linkDistance", fo.linkDistance, base.forces.linkDistance),
    },
  };
}

function hydrateLocal(raw: unknown): LocalGraphSettings {
  const base = hydrateSettings(raw, DEFAULT_LOCAL_SETTINGS);
  const l = ((raw ?? {}) as { local?: Partial<GraphLocalOptions> }).local ?? {};
  return {
    ...base,
    local: {
      depth: clampTo("depth", l.depth, DEFAULT_LOCAL.depth),
      incoming: bool(l.incoming, DEFAULT_LOCAL.incoming),
      outgoing: bool(l.outgoing, DEFAULT_LOCAL.outgoing),
      neighborLinks: bool(l.neighborLinks, DEFAULT_LOCAL.neighborLinks),
    },
  };
}

interface GraphStore {
  global: GraphSettings;
  local: LocalGraphSettings;
  collapsed: Record<GraphSection, boolean>;
  setFilters: (scope: GraphScope, patch: Partial<GraphFilters>) => void;
  setDisplay: (scope: GraphScope, patch: Partial<GraphDisplay>) => void;
  setForces: (scope: GraphScope, patch: Partial<GraphForces>) => void;
  setLocalOptions: (patch: Partial<GraphLocalOptions>) => void;
  addGroup: (scope: GraphScope) => string;
  updateGroup: (scope: GraphScope, id: string, patch: Partial<Omit<GraphGroup, "id">>) => void;
  removeGroup: (scope: GraphScope, id: string) => void;
  reorderGroup: (scope: GraphScope, id: string, toIndex: number) => void;
  restoreDefaults: (scope: GraphScope) => void;
  toggleSection: (section: GraphSection) => void;
}

type SettingsKey = "filters" | "display" | "forces";

function withSection<T extends GraphSettings>(s: T, key: SettingsKey, patch: object): T {
  return { ...s, [key]: { ...s[key], ...patch } };
}

export const useGraph = create<GraphStore>()(
  persist(
    (set) => {
      const patchSection = (scope: GraphScope, key: SettingsKey, patch: object) =>
        set((s) =>
          scope === "global"
            ? { global: withSection(s.global, key, patch) }
            : { local: withSection(s.local, key, patch) },
        );

      const patchGroups = (scope: GraphScope, fn: (groups: GraphGroup[]) => GraphGroup[]) =>
        set((s) => {
          const cur = scope === "global" ? s.global : s.local;
          const groups = fn(cur.groups);
          return scope === "global" ? { global: { ...s.global, groups } } : { local: { ...s.local, groups } };
        });

      return {
        global: freshSettings(),
        local: freshLocal(),
        collapsed: { filters: false, groups: false, display: false, forces: false, local: false },

        setFilters: (scope, patch) => patchSection(scope, "filters", patch),

        setDisplay: (scope, patch) => {
          const clean: Partial<GraphDisplay> = { ...patch };
          for (const k of ["textFadeMultiplier", "nodeSizeMultiplier", "lineSizeMultiplier"] as const) {
            if (clean[k] !== undefined) clean[k] = clampTo(k, clean[k], DEFAULT_DISPLAY[k]);
          }
          patchSection(scope, "display", clean);
        },

        setForces: (scope, patch) => {
          const clean: Partial<GraphForces> = { ...patch };
          for (const k of ["centerStrength", "repelStrength", "linkStrength", "linkDistance"] as const) {
            if (clean[k] !== undefined) clean[k] = clampTo(k, clean[k], DEFAULT_FORCES[k]);
          }
          patchSection(scope, "forces", clean);
        },

        setLocalOptions: (patch) =>
          set((s) => ({
            local: {
              ...s.local,
              local: {
                ...s.local.local,
                ...patch,
                ...(patch.depth !== undefined
                  ? { depth: clampTo("depth", patch.depth, DEFAULT_LOCAL.depth) }
                  : {}),
              },
            },
          })),

        addGroup: (scope) => {
          const id = newId();
          patchGroups(scope, (groups) => {
            if (groups.length >= MAX_GROUPS) return groups;
            const color = GROUP_SEED_COLORS[groups.length % GROUP_SEED_COLORS.length]!;
            return [...groups, { id, query: "", color }];
          });
          return id;
        },

        updateGroup: (scope, id, patch) =>
          patchGroups(scope, (groups) => groups.map((g) => (g.id === id ? { ...g, ...patch } : g))),

        removeGroup: (scope, id) => patchGroups(scope, (groups) => groups.filter((g) => g.id !== id)),

        reorderGroup: (scope, id, toIndex) =>
          patchGroups(scope, (groups) => {
            const from = groups.findIndex((g) => g.id === id);
            if (from < 0) return groups;
            const next = [...groups];
            const [moved] = next.splice(from, 1);
            if (!moved) return groups;
            next.splice(Math.max(0, Math.min(next.length, toIndex)), 0, moved);
            return next;
          }),

        restoreDefaults: (scope) => {
          resetPositions(scope);
          set(scope === "global" ? { global: freshSettings() } : { local: freshLocal() });
        },

        toggleSection: (section) =>
          set((s) => ({ collapsed: { ...s.collapsed, [section]: !s.collapsed[section] } })),
      };
    },
    {
      name: "hn.graph",
      version: 1,
      partialize: (s) => ({ global: s.global, local: s.local, collapsed: s.collapsed }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<GraphStore>;
        return {
          ...current,
          global: hydrateSettings(p.global, DEFAULT_GLOBAL),
          local: hydrateLocal(p.local),
          collapsed: { ...current.collapsed, ...(p.collapsed ?? {}) },
        };
      },
    },
  ),
);
